/**
 * What the session's fills and order history share (#1809, WS_DESIGN_1809 W6, W7): rows read once, kept current by a
 * stream, for as long as the account stays on screen. Dropping the session drops them. "More" only shows what is held.
 *
 * The download starts `delayMs` after the session does, so hopping between ghosts reads nothing, and queues on the pacer.
 *
 * A resubscribe sends the newest 30 rows again. If one of them is held, the rows are continuous and no REST is sent. If
 * not, what is missing is read once. That is a repeat refresh, so it charges the pacer without queueing (as a settlement
 * refresh does) and may leave debt for the next reader.
 *
 * A snapshot that lands before the download answers is compared with the answer instead: a read sent before a socket
 * drop knows nothing of what happened during it.
 *
 * A sleeping tab reads nothing (#1809 W12): reads and their timers stop, and a read still owed waits for the return. The
 * socket's reopening is a reconnect, so what was missed is found as above.
 */
import { isAbortError, type InfoClient } from '../hl/info';
import type { WeightPacer } from '../hl/weightPacer';
import type { WsManager } from '../hl/ws';

export interface SessionRowsDeps {
  ws: WsManager;
  info: InfoClient;
  pacer: WeightPacer;
  user: string;
  delayMs: number;
  /** Wait for a snapshot after a reconnect before reading anyway. */
  fallbackMs: number;
  /** Wait before a failed read is tried again. */
  retryMs: number;
}

export interface RowsStatus {
  /** True until the first rows or the first failed read. */
  loading: boolean;
  /** The download has answered: no more rows are on their way. */
  complete: boolean;
  error: string | null;
}

export abstract class SessionRows<S extends RowsStatus, R> {
  private readonly listeners = new Set<() => void>();
  private running = false;
  private offs: (() => void)[] = [];
  private reading: AbortController | null = null;
  private readTimer: ReturnType<typeof setTimeout> | null = null;
  private snapshotTimer: ReturnType<typeof setTimeout> | null = null;
  /** The last snapshot, until the download has answered. */
  private snapshot: R[] | null = null;
  private asleep = false;

  protected constructor(
    protected readonly deps: SessionRowsDeps,
    protected state: S,
  ) {}

  /** Holds the stream of these rows; calls onRows with each frame. */
  protected abstract stream(): () => void;
  /** Nothing was read yet, or a read is still owed. */
  protected abstract readOwed(): boolean;
  /** The owed read. */
  protected abstract read(): void;
  /** Reads what may be missing after the held rows. */
  protected abstract readMissing(): void;
  protected abstract keyOf(row: R): string | number;
  protected abstract held(): readonly R[];
  /** The state patch that merges `rows` into the held ones. */
  protected abstract merged(rows: R[]): Partial<S>;

  readonly getState = (): S => this.state;

  readonly subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  start(): void {
    if (this.running) return;
    this.running = true;
    const { ws } = this.deps;
    this.offs = [this.stream(), ws.onReconnect(() => this.onReconnect()), ws.onStateChange(() => this.followSleep())];
    if (this.readOwed()) this.readAfter(this.deps.delayMs);
  }

  stop(): void {
    this.running = false;
    for (const off of this.offs) off();
    this.offs = [];
    this.stopSnapshotTimer();
    this.stopReadTimer();
    this.cancelRead();
  }

  private followSleep(): void {
    const { ws, delayMs } = this.deps;
    if (ws.suspended === this.asleep) return;
    this.asleep = ws.suspended;
    if (this.asleep) {
      this.stopSnapshotTimer();
      this.stopReadTimer();
      this.cancelRead();
    } else if (this.readOwed()) this.readAfter(delayMs);
  }

  /** A failed read goes now instead of after its wait. */
  retry(): void {
    if (this.state.error !== null && this.readTimer !== null) this.readAfter(0);
  }

  protected onRows(rows: R[], isSnapshot: boolean): void {
    if (isSnapshot) this.onSnapshot(rows);
    if (rows.length > 0) this.publish({ ...this.merged(rows), loading: false });
    else if (this.state.loading) this.publish({ loading: false } as Partial<S>);
  }

  private onSnapshot(rows: R[]): void {
    this.stopSnapshotTimer();
    if (!this.state.complete) this.snapshot = rows;
    else if (rows.length > 0 && !this.overlaps(this.held(), rows)) this.readMissing();
  }

  /** The subscription was sent again, so a snapshot is on its way. Without one nothing shows what was missed. */
  private onReconnect(): void {
    this.stopSnapshotTimer();
    this.snapshotTimer = setTimeout(() => {
      this.snapshotTimer = null;
      if (this.state.complete) this.readMissing();
    }, this.deps.fallbackMs);
  }

  /** The download's answer is in: true when the snapshot that came meanwhile does not join on to it. */
  protected missedDuringRead(answer: readonly R[]): boolean {
    const snapshot = this.snapshot;
    this.snapshot = null;
    return !!snapshot?.length && !this.overlaps(answer, snapshot);
  }

  protected overlaps(a: readonly R[], b: readonly R[]): boolean {
    const keys = new Set(a.map((r) => this.keyOf(r)));
    return b.some((r) => keys.has(this.keyOf(r)));
  }

  private readAfter(ms: number): void {
    this.stopReadTimer();
    this.readTimer = setTimeout(() => {
      this.readTimer = null;
      this.read();
    }, ms);
  }

  protected beginRead(): AbortController {
    this.cancelRead();
    this.stopReadTimer();
    const ac = new AbortController();
    this.reading = ac;
    return ac;
  }

  protected failed(ac: AbortController, e: unknown): void {
    if (ac.signal.aborted || isAbortError(e)) return;
    this.publish({ loading: false, error: e instanceof Error ? e.message : String(e) } as Partial<S>);
    this.readAfter(this.deps.retryMs);
  }

  private cancelRead(): void {
    this.reading?.abort();
    this.reading = null;
  }

  private stopSnapshotTimer(): void {
    if (this.snapshotTimer) clearTimeout(this.snapshotTimer);
    this.snapshotTimer = null;
  }

  private stopReadTimer(): void {
    if (this.readTimer) clearTimeout(this.readTimer);
    this.readTimer = null;
  }

  protected publish(patch: Partial<S>): void {
    this.state = { ...this.state, ...patch };
    for (const l of [...this.listeners]) l();
  }
}
